import { Avatar } from '@src/components/base/Avatar';
import { Body2 } from '@src/components/typography/Body2';
import { Body4 } from '@src/components/typography/Body4';
import { Button } from '@src/components/Button';
import { ButtonType } from '@src/types';
import { IconType } from '@src/IconType';
import { type PropsWithChildren, useCallback, useState } from 'react';

type RecruiterItemProps = {
  avatar: string | null;
  displayName: string;
  email: string;
  fallBackToInitials?: boolean;
  disabled?: boolean;
  onDelete?: () => Promise<void>;
};

export const RecruiterItem = ({
  avatar,
  displayName,
  email,
  fallBackToInitials = false,
  disabled,
  onDelete,
  children,
}: PropsWithChildren<RecruiterItemProps>) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!onDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete]);

  return (
    <div className="flex items-center gap-4" data-testid="recruiter-item">
      <Avatar
        name={fallBackToInitials ? displayName : undefined}
        variant="circular"
        disabled
        type="directusFile"
        asset={avatar ?? undefined}
        className="hidden sm:contents"
      />
      <span className="grow">
        <Body2>{displayName}</Body2>
        <Body4 className="!text-slate-500">{email}</Body4>
      </span>
      <span className="flex gap-2">
        {children}
        <Button
          buttonType={ButtonType.SECONDARY}
          IconLeading={IconType.TrashAlt}
          iconClassName="size-6"
          className="!py-2.5 !px-3"
          disabled={!onDelete || disabled}
          loading={isDeleting}
          onClick={handleDelete}
          testId="delete-recruiter-button"
        />
      </span>
    </div>
  );
};
